import { Box, HStack, Text, VStack } from "@chakra-ui/react";
import EntityModalButton from "./EntityModalButton";
import React from "react";
import { entityProfileModalColors } from "../entityProfileModal.theme";

function formatEditableValue(value: React.ReactNode, shouldShowEmptyPlaceholder: boolean) {
  if (value === null || value === undefined || value === "") {
    if (!shouldShowEmptyPlaceholder) {
      return "";
    }

    return (
      <Text as="span" color={entityProfileModalColors.empty} fontStyle="italic">
        empty
      </Text>
    );
  }

  return value;
}

type Props<TField extends string> = {
  activeField: TField | null;
  children: React.ReactNode;
  cancelOnBlur?: boolean;
  emptyPlaceholder?: boolean;
  field: TField;
  isEditable?: boolean;
  label: string;
  variant?: "split" | "compact";
  value: React.ReactNode;
  onActivate: (field: TField) => void;
  onCancel: () => void;
  onCommit: () => void;
};

export default function EntityProfileInlineEditableRow<TField extends string>(
  props: Props<TField>
) {
  const {
    activeField,
    cancelOnBlur,
    children,
    emptyPlaceholder,
    field,
    isEditable,
    label,
    onActivate,
    onCancel,
    onCommit,
    value,
    variant = "split",
  } = props;
  const isActive = activeField === field;
  const isClickable = isEditable !== false && !isActive;
  const shouldShowEmptyPlaceholder = emptyPlaceholder ?? true;
  const rowRef = React.useRef<HTMLDivElement>(null);
  const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onActivate(field);
    }
  };

  React.useEffect(() => {
    if (!isActive || cancelOnBlur !== false) {
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target;

      if (!(target instanceof Element)) {
        return;
      }

      if (rowRef.current?.contains(target) || target.closest("[data-popper-placement]") !== null) {
        return;
      }

      onCancel();
    };

    document.addEventListener("mousedown", handlePointerDown, true);

    return () => document.removeEventListener("mousedown", handlePointerDown, true);
  }, [cancelOnBlur, isActive, onCancel]);

  if (variant === "compact") {
    const handleEditorKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onCancel();
        return;
      }

      if (event.key !== "Enter" || event.target instanceof HTMLTextAreaElement) {
        return;
      }

      event.preventDefault();
      onCommit();
    };

    return (
      <Box
        ref={rowRef}
        onBlur={
          cancelOnBlur === false
            ? undefined
            : (event) => {
                if (!isActive || event.currentTarget.contains(event.relatedTarget as Node | null)) {
                  return;
                }

                onCancel();
              }
        }
      >
        <HStack alignItems="baseline" minH="28px" spacing={1}>
          <Text
            _hover={isClickable ? { color: entityProfileModalColors.active } : undefined}
            color={isActive ? entityProfileModalColors.active : entityProfileModalColors.bodyText}
            cursor={isClickable ? "pointer" : undefined}
            fontSize="14px"
            fontWeight={700}
            role={isClickable ? "button" : undefined}
            tabIndex={isClickable ? 0 : undefined}
            onClick={isClickable ? () => onActivate(field) : undefined}
            onKeyDown={isClickable ? handleKeyDown : undefined}
          >
            {label}:
          </Text>
          <Box
            _hover={isClickable ? { color: entityProfileModalColors.active } : undefined}
            color={isActive ? entityProfileModalColors.active : entityProfileModalColors.text}
            cursor={isClickable ? "pointer" : undefined}
            fontSize="14px"
            minW={0}
            role={isClickable ? "button" : undefined}
            tabIndex={isClickable ? 0 : undefined}
            onClick={isClickable ? () => onActivate(field) : undefined}
            onKeyDown={isClickable ? handleKeyDown : undefined}
          >
            {formatEditableValue(value, shouldShowEmptyPlaceholder)}
          </Box>
        </HStack>
        {isActive ? (
          <HStack
            alignItems="flex-start"
            minH="42px"
            pb={2}
            pt={2}
            spacing={1}
            onKeyDown={handleEditorKeyDown}
          >
            <Box flex="0 1 auto" minW={0}>
              {children}
            </Box>
            <EntityModalButton
              isIconOnly
              aria-label={`Save ${label}`}
              iconClassName="fa fa-check"
              kind="blue"
              size="xs"
              title="Save"
              onClick={onCommit}
            />
            <EntityModalButton
              isIconOnly
              aria-label={`Cancel ${label}`}
              iconClassName="fa fa-times"
              kind="white"
              size="xs"
              title="Cancel"
              onClick={onCancel}
            />
          </HStack>
        ) : null}
      </Box>
    );
  }

  return (
    <Box ref={rowRef}>
      <HStack
        borderBottom={isActive ? "none" : `1px solid ${entityProfileModalColors.border}`}
        justifyContent="space-between"
        minH="42px"
        spacing={6}
      >
        <VStack alignItems="flex-start" spacing={1}>
          <Text
            _hover={
              isClickable
                ? { color: entityProfileModalColors.active, textDecoration: "underline" }
                : undefined
            }
            color={isActive ? entityProfileModalColors.active : entityProfileModalColors.labelText}
            cursor={isClickable ? "pointer" : undefined}
            fontSize="14px"
            fontWeight={700}
            role={isClickable ? "button" : undefined}
            tabIndex={isClickable ? 0 : undefined}
            onClick={isClickable ? () => onActivate(field) : undefined}
            onKeyDown={isClickable ? handleKeyDown : undefined}
          >
            {label}
          </Text>
        </VStack>
        <HStack flex="1 1 auto" justifyContent="flex-end" minW={0} spacing={3}>
          <Box
            _hover={
              isClickable
                ? { color: entityProfileModalColors.active, textDecoration: "underline" }
                : undefined
            }
            color={isActive ? entityProfileModalColors.active : entityProfileModalColors.muted}
            cursor={isClickable ? "pointer" : undefined}
            fontSize="14px"
            fontWeight={isActive ? 700 : undefined}
            maxW="420px"
            minW={0}
            role={isClickable ? "button" : undefined}
            tabIndex={isClickable ? 0 : undefined}
            textAlign="right"
            onClick={isClickable ? () => onActivate(field) : undefined}
            onKeyDown={isClickable ? handleKeyDown : undefined}
          >
            {formatEditableValue(value, shouldShowEmptyPlaceholder)}
          </Box>
        </HStack>
      </HStack>
      {isActive ? (
        <HStack
          borderBottom={`1px solid ${entityProfileModalColors.border}`}
          justifyContent="space-between"
          minH="42px"
          spacing={6}
          onBlur={
            cancelOnBlur === false
              ? undefined
              : (event) => {
                  if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
                    onCancel();
                  }
                }
          }
        >
          <Text
            color={entityProfileModalColors.labelText}
            fontSize="14px"
            fontWeight={700}
            minW={0}
          />
          <HStack flex="0 1 420px" justifyContent="flex-end" minW={0} spacing={3}>
            <Box display="flex" flex="1 1 auto" justifyContent="flex-end" minW={0}>
              {children}
            </Box>
            <HStack spacing={1}>
              <EntityModalButton
                isIconOnly
                aria-label={`Save ${label}`}
                iconClassName="fa fa-check"
                kind="blue"
                size="xs"
                title="Save"
                onClick={onCommit}
              />
              <EntityModalButton
                isIconOnly
                aria-label={`Cancel ${label}`}
                iconClassName="fa fa-times"
                kind="white"
                size="xs"
                title="Cancel"
                onClick={onCancel}
              />
            </HStack>
          </HStack>
        </HStack>
      ) : null}
    </Box>
  );
}
